import React from 'react';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
} from 'recharts';
import { formatNumber, PALETTE, CHART_TOOLTIP } from './format';
import { EMPTY_TEXT } from '../copy';

// Donut chart with a centre label. Used for tier-distribution, channel-mix,
// provider-mix.
//
// Props:
//   data        — [{name, value, color?}]
//   height      — px (default 220)
//   centerLabel — string shown in the middle (e.g. total)
//   centerSub   — small label below centerLabel (e.g. "users")
//   colors      — optional palette override
//   formatVal   — formatter for tooltip values (default formatNumber)
//   legend      — false to draw the ring alone, when the caller shows the figures itself
export default function Donut({
  data,
  height = 220,
  centerLabel,
  centerSub,
  colors,
  formatVal = formatNumber,
  legend = true,
}) {
  if (!data || data.length === 0 || data.every(d => !d.value)) {
    return <div className="tile-empty">{EMPTY_TEXT}</div>;
  }
  const palette = colors || PALETTE;
  const total = data.reduce((s, d) => s + (Number(d.value) || 0), 0);
  return (
    // The legend sits OUTSIDE the height-bound chart wrapper, in normal flow, so
    // it never overflows the card.
    <div className="donut">
      <div className="donut-plot" style={{ height }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius="60%"
              outerRadius="88%"
              paddingAngle={1.5}
              stroke="var(--surface)"
              strokeWidth={2}
              isAnimationActive={false}
            >
              {data.map((entry, i) => (
                <Cell key={i} fill={entry.color || palette[i % palette.length]} />
              ))}
            </Pie>
            <Tooltip
              formatter={(v, n) => [formatVal(v), n]}
              contentStyle={CHART_TOOLTIP.contentStyle}
              itemStyle={CHART_TOOLTIP.itemStyle}
              labelStyle={CHART_TOOLTIP.labelStyle}
            />
          </PieChart>
        </ResponsiveContainer>
        {centerLabel !== undefined && (
          <div className="donut-center" aria-hidden="true">
            <div className="donut-center-value">{centerLabel}</div>
            {centerSub && <div className="donut-center-sub">{centerSub}</div>}
          </div>
        )}
      </div>
      {legend && <ul className="donut-legend">
        {data.map((d, i) => (
          <li key={d.name}>
            <span className="chart-legend-swatch" style={{ background: d.color || palette[i % palette.length] }} />
            <span className="donut-legend-name">{d.name}</span>
            <span className="donut-legend-value">{formatVal(d.value)}</span>
            {total > 0 && (
              <span className="donut-legend-pct">{Math.round(((Number(d.value) || 0) / total) * 100)}%</span>
            )}
          </li>
        ))}
      </ul>}
    </div>
  );
}
